import React, { useEffect, useState, Fragment } from "react";
import { useTranslation } from 'react-i18next';
import { useNavigate } from "react-router-dom";
import { Col, Row, Label, FormGroup, Card, CardBody } from "reactstrap";
import { Form } from "react-final-form";
import { CKEditor } from "ckeditor4-react";
import LoadingOverlay from "react-loading-overlay-ts";
import DNALoader from "../../components/Loaders/DnaLoader";
import "../../assets/custom css/loadoverlay.scss";
import Alerts from "../../components/Alerts/alerts";
import ConfigurationService from "./configuration.service";
import AuthService from "../Authentication/auth.service";
import PermissionAccess from '../../PermissionAccess/Access.js';

const TabPrivacyPolicy = () => {
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [configs, setConfigs] = useState({});
    const [dataLoaded, setDataLoaded] = useState(false);

    const [pharmacyTerms, setPharmacyTerms] = useState("");
    const [doctorTerms, setDoctorTerms] = useState("");
    const [patientTerms, setPatientTerms] = useState("");

    const { authData } = PermissionAccess();

    useEffect(() => {
        i18n.changeLanguage(localStorage.getItem('language'));
        getSystemConfig();
    }, []);

    function getSystemConfig() {
        setLoading(true);
        ConfigurationService.getConfiguration(authData.sessionID).then((response) => {
            setLoading(false);
            if (response.data.status) {
                const rec_data = response.data.data[0];
                setConfigs(rec_data);
                setPharmacyTerms(rec_data.privacy_policy_pharmacy || "");
                setDoctorTerms(rec_data.privacy_policy_doctor || "");
                setPatientTerms(rec_data.privacy_policy_patient || "");
                setDataLoaded(true);
            } else {
                if (response.data.message === "Unauthorized") {
                    AuthService.logout();
                    navigate("/login");
                    window.location.reload();
                } else {
                    Alerts.swalErrorAlert(t("Unable to retreive configurations at the moment."));
                }
            }
        }, (error) => {
            setLoading(false);
            Alerts.swalErrorAlert(t("Unable to retreive configurations at the moment."));
        });
    }

    const onSubmit = () => {
        const payloadData = {
            functionName: "SetConfigurations",
            postData: {
                ...configs,
                privacy_policy_pharmacy: pharmacyTerms,
                privacy_policy_doctor: doctorTerms,
                privacy_policy_patient: patientTerms
            }
        };

        setLoading(true);
        ConfigurationService.setConfiguration(authData.sessionID, payloadData).then((response) => {
            setLoading(false);
            if (response.data.status) {
                Alerts.swalSuccessAlert(t("Privacy Policy updated successfully."));
            } else {
                Alerts.swalErrorAlert(response.data.message || t("Failed to update configurations."));
            }
        }, (error) => {
            setLoading(false);
            Alerts.swalErrorAlert(t("Network error. Please try again."));
        });
    };

    return (
        <Fragment>
            <div className="container-fluid">
                <Row>
                    <Col md={12}>
                        <Card className="main-card mb-3 border-0 shadow-sm" style={{ borderRadius: '15px' }}>
                            <CardBody className="p-4">
                                <div className="d-flex justify-content-between align-items-center mb-4">
                                    <div>
                                        <h4 className="fw-bold text-dark mb-1">{t('Privacy Policy Settings')}</h4>
                                        <p className="text-muted mb-0">{t('Manage and update your privacy policy content for all apps.')}</p>
                                    </div>
                                </div>

                                <LoadingOverlay tag="div" active={loading}
                                    styles={{ overlay: (base) => ({ ...base, borderRadius: '15px' }) }}
                                    spinner={<DNALoader />}>

                                    <Row>
                                        {/* Pharmacy App */}
                                        <Col md={12} className="mb-4">
                                            <Card className="border-0 bg-light" style={{ borderRadius: '12px' }}>
                                                <CardBody>
                                                    <h5 className="fw-semibold mb-3 d-flex align-items-center">
                                                        <i className="fas fa-prescription-bottle-alt me-2 text-primary"></i>
                                                        {t('Pharmacy App Privacy Policy')}
                                                    </h5>
                                                    <FormGroup>
                                                        {dataLoaded && (
                                                            <CKEditor
                                                                initData={pharmacyTerms}
                                                                onChange={(evt) => setPharmacyTerms(evt.editor.getData())}
                                                                config={{
                                                                    height: 300,
                                                                    removeButtons: 'About'
                                                                }}
                                                            />
                                                        )}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Doctor App */}
                                        <Col md={12} className="mb-4">
                                            <Card className="border-0 bg-light" style={{ borderRadius: '12px' }}>
                                                <CardBody>
                                                    <h5 className="fw-semibold mb-3 d-flex align-items-center">
                                                        <i className="fas fa-user-md me-2 text-info"></i>
                                                        {t('Doctor App Privacy Policy')}
                                                    </h5>
                                                    <FormGroup>
                                                        {dataLoaded && (
                                                            <CKEditor
                                                                initData={doctorTerms}
                                                                onChange={(evt) => setDoctorTerms(evt.editor.getData())}
                                                                config={{
                                                                    height: 300,
                                                                    removeButtons: 'About'
                                                                }}
                                                            />
                                                        )}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>

                                        {/* Patient App */}
                                        <Col md={12} className="mb-4">
                                            <Card className="border-0 bg-light" style={{ borderRadius: '12px' }}>
                                                <CardBody>
                                                    <h5 className="fw-semibold mb-3 d-flex align-items-center">
                                                        <i className="fas fa-user-injured me-2 text-success"></i>
                                                        {t('Patient App Privacy Policy')}
                                                    </h5>
                                                    <FormGroup>
                                                        {dataLoaded && (
                                                            <CKEditor
                                                                initData={patientTerms}
                                                                onChange={(evt) => setPatientTerms(evt.editor.getData())}
                                                                config={{
                                                                    height: 300,
                                                                    removeButtons: 'About'
                                                                }}
                                                            />
                                                        )}
                                                    </FormGroup>
                                                </CardBody>
                                            </Card>
                                        </Col>
                                    </Row>

                                    {/* Action Button */}
                                    <div className="d-flex justify-content-center mt-4">
                                        <button
                                            className="btn btn-lg px-5 py-3"
                                            type="button"
                                            onClick={onSubmit}
                                            style={{
                                                backgroundColor: 'var(--buttons-color)',
                                                borderColor: 'var(--buttons-color)',
                                                color: 'var(--buttons-text-color)',
                                                borderRadius: '15px',
                                                fontWeight: '600',
                                                minWidth: '200px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                                            }}
                                        >
                                            <i className="fas fa-save me-2"></i>
                                            {t('Save Privacy Policy')}
                                        </button>
                                    </div>
                                </LoadingOverlay>
                            </CardBody>
                        </Card>
                    </Col>
                </Row>
            </div>
        </Fragment>
    );
};

export default TabPrivacyPolicy;
